/**
 * Meeting Spots Page
 * Family meeting points for emergencies or meetups
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, MapPin, Trash2, Edit2, Check, X } from 'lucide-react';
import { useMeetingSpotsStore } from '../store';
import styles from './MeetingSpots.module.css';

const EMOJI_OPTIONS = ['📍', '⭐', '🏠', '🚩', '💜', '💚', '🔵', '🎯'];

export function MeetingSpotsPage() {
  const navigate = useNavigate();
  const spots = useMeetingSpotsStore((state) => state.spots);
  const addSpot = useMeetingSpotsStore((state) => state.addSpot);
  const updateSpot = useMeetingSpotsStore((state) => state.updateSpot);
  const removeSpot = useMeetingSpotsStore((state) => state.removeSpot);

  const [isAddingSpot, setIsAddingSpot] = useState(false);
  const [newSpotName, setNewSpotName] = useState('');
  const [newSpotEmoji, setNewSpotEmoji] = useState('📍');
  const [dropPosition, setDropPosition] = useState<{ x: number; y: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const mapRef = useRef<HTMLDivElement>(null);

  const handleMapClick = (e: React.MouseEvent) => {
    if (!isAddingSpot || !mapRef.current) return;

    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);

    setDropPosition({ x, y });
  };

  const handleCaptureLocation = () => {
    // Simulate current location (center of map for demo)
    // In production, would use actual GPS
    setDropPosition({ x: 50, y: 60 });
  };

  const handleSaveSpot = () => {
    if (!dropPosition || !newSpotName.trim()) return;

    addSpot({
      name: newSpotName.trim(),
      x: dropPosition.x,
      y: dropPosition.y,
      emoji: newSpotEmoji,
    });

    // Reset
    setIsAddingSpot(false);
    setNewSpotName('');
    setNewSpotEmoji('📍');
    setDropPosition(null);
  };

  const handleStartEdit = (spot: typeof spots[0]) => {
    setEditingId(spot.id);
    setEditName(spot.name);
  };

  const handleSaveEdit = (id: string) => {
    if (editName.trim()) {
      updateSpot(id, { name: editName.trim() });
    }
    setEditingId(null);
    setEditName('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className={styles.title}>Meeting Spots</h1>
        <div style={{ width: 24 }} />
      </header>

      <div className={styles.content}>
        <p className={styles.subtitle}>
          Set family meeting points so everyone knows where to find each other!
        </p>

        {/* Mini Map for adding/viewing spots */}
        <div className={styles.mapSection}>
          <div
            ref={mapRef}
            className={`${styles.miniMap} ${isAddingSpot ? styles.miniMapActive : ''}`}
            onClick={handleMapClick}
          >
            <img src="/stad.jpg" alt="Stadium" className={styles.mapImage} />

            {/* Existing spots */}
            {spots.map((spot) => (
              <div
                key={spot.id}
                className={styles.spotMarker}
                style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
              >
                {spot.emoji}
              </div>
            ))}

            {/* New spot being placed */}
            {isAddingSpot && dropPosition && (
              <div
                className={`${styles.spotMarker} ${styles.spotMarkerNew}`}
                style={{ left: `${dropPosition.x}%`, top: `${dropPosition.y}%` }}
              >
                {newSpotEmoji}
              </div>
            )}

            {isAddingSpot && (
              <div className={styles.mapOverlay}>
                Tap map to place marker
              </div>
            )}
          </div>
        </div>

        {/* Add Spot Mode */}
        {isAddingSpot ? (
          <div className={styles.addSpotForm}>
            <div className={styles.formRow}>
              <input
                type="text"
                className={styles.nameInput}
                placeholder="Spot name (e.g., Main Gate)"
                value={newSpotName}
                onChange={(e) => setNewSpotName(e.target.value)}
                autoFocus
              />
            </div>

            <div className={styles.emojiRow}>
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  className={`${styles.emojiButton} ${newSpotEmoji === emoji ? styles.emojiButtonActive : ''}`}
                  onClick={() => setNewSpotEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>

            <button
              className={styles.captureButton}
              onClick={handleCaptureLocation}
            >
              <MapPin size={18} />
              Capture My Current Location
            </button>

            <div className={styles.formActions}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setIsAddingSpot(false);
                  setDropPosition(null);
                  setNewSpotName('');
                }}
              >
                Cancel
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSaveSpot}
                disabled={!dropPosition || !newSpotName.trim()}
              >
                Save Spot
              </button>
            </div>
          </div>
        ) : (
          <button
            className={styles.addButton}
            onClick={() => setIsAddingSpot(true)}
          >
            <Plus size={20} />
            Add Meeting Spot
          </button>
        )}

        {/* Saved Spots List */}
        {spots.length > 0 && (
          <div className={styles.spotsList}>
            <h2 className={styles.listTitle}>Your Spots</h2>
            {spots.map((spot) => (
              <div key={spot.id} className={styles.spotCard}>
                <span className={styles.spotEmoji}>{spot.emoji}</span>
                {editingId === spot.id ? (
                  <input
                    type="text"
                    className={styles.editInput}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                  />
                ) : (
                  <span className={styles.spotName}>{spot.name}</span>
                )}
                <div className={styles.spotActions}>
                  {editingId === spot.id ? (
                    <>
                      <button
                        className={styles.actionButton}
                        onClick={() => handleSaveEdit(spot.id)}
                      >
                        <Check size={18} />
                      </button>
                      <button
                        className={styles.actionButton}
                        onClick={handleCancelEdit}
                      >
                        <X size={18} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className={styles.actionButton}
                        onClick={() => handleStartEdit(spot)}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        onClick={() => removeSpot(spot.id)}
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {spots.length === 0 && !isAddingSpot && (
          <div className={styles.emptyState}>
            <span className={styles.emptyEmoji}>👨‍👩‍👧‍👦</span>
            <p>No meeting spots yet</p>
            <p className={styles.emptyHint}>
              Add spots so your family knows where to meet!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
